import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Camera, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = ["instagram", "tiktok", "youtube", "other"];

export function EventContentSubmit({ opportunityId, initial = [], onChange }) {
  const [items, setItems] = useState(initial);
  const [open, setOpen] = useState(initial.length === 0);
  const [platform, setPlatform] = useState("instagram");
  const [postUrl, setPostUrl] = useState("");
  const [reach, setReach] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!postUrl.trim()) { toast.error("Add the link to your post"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/opportunities/${opportunityId}/content`, {
        platform, post_url: postUrl.trim(), reach: parseInt(reach || "0", 10) || 0,
      });
      const next = [r.data, ...items];
      setItems(next); onChange && onChange(next);
      setPostUrl(""); setReach(""); setOpen(false);
      toast.success("Content added 🎉 Thanks for sharing!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (cid) => {
    try {
      await api.delete(`/opportunities/${opportunityId}/content/${cid}`);
      const next = items.filter((x) => x.id !== cid);
      setItems(next); onChange && onChange(next);
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  return (
    <div data-testid="event-content-submit" className="mt-4 rounded-2xl border border-fuchsia-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <Camera className="h-5 w-5 text-fuchsia-600" />
        <h3 className="font-display font-bold text-zinc-900">Share your event content</h3>
      </div>
      <p className="mt-1 text-sm text-zinc-500">Add the posts you created so the organiser can see the reach you brought. Numbers are self-reported for now.</p>

      {items.length > 0 && (
        <div className="mt-3 space-y-2">
          {items.map((c) => (
            <div key={c.id} data-testid={`my-content-${c.id}`} className="flex items-center justify-between gap-3 rounded-xl bg-fuchsia-50/70 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-zinc-800"><span className="capitalize">{c.platform || "post"}</span> · {(c.reach || 0).toLocaleString()} reach</p>
                <a href={c.post_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 truncate text-xs text-fuchsia-700 hover:underline">
                  <ExternalLink className="h-3 w-3" /> {c.post_url}
                </a>
              </div>
              <button data-testid={`del-content-${c.id}`} onClick={() => remove(c.id)} className="shrink-0 text-zinc-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      {open ? (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {PLATFORMS.map((p) => (
              <button key={p} data-testid={`content-platform-${p}`} onClick={() => setPlatform(p)}
                className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${platform === p ? "bg-fuchsia-600 text-white" : "bg-zinc-100 text-zinc-600"}`}>{p}</button>
            ))}
          </div>
          <Input data-testid="content-url" value={postUrl} onChange={(e) => setPostUrl(e.target.value)} placeholder="Link to your post (https://…)" />
          <Input data-testid="content-reach" type="number" min="0" value={reach} onChange={(e) => setReach(e.target.value)} placeholder="Reach / views (e.g. 4200)" />
          <div className="flex gap-2">
            <Button data-testid="content-save" onClick={submit} disabled={busy} className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Add content</>}
            </Button>
            {items.length > 0 && <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>}
          </div>
        </div>
      ) : (
        <Button data-testid="content-add-another" variant="outline" onClick={() => setOpen(true)} className="mt-3 rounded-full border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50">
          <Plus className="mr-1.5 h-4 w-4" /> Add {items.length > 0 ? "another" : "a"} post
        </Button>
      )}
    </div>
  );
}
